/** Drillz tenant view used for dashboard campaign progress metrics. */
export const DRILLZ_CAMPAIGN_PROGRESS_VIEW = 'tenant_data.v_drillz_campaign_progress';

/** Legacy dashboard data sources for top-panel tooltips. */
export const DASHBOARD_DATA_SOURCES = {
  drillingProgress: 'drilling_progress.mv_drilling_progress_metrics',
  sampleProgress: 'sample_progress.mv_sample_progress',
  projectTarget: 'drilling_scmi.project_sources.target',
  holeSumDrilled: 'drilling_progress.mv_drilling_progress_metrics (hole depth sum)',
  holeSumLogged: 'drilling_progress.mv_drilling_progress_metrics (logged_depth_m sum)',
};

export function sourceTooltip(tableName) {
  return `Source: ${tableName}`;
}

function parseDrillzNumber(value) {
  if (value == null || value === '') return 0;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function parseDrillzInt(value) {
  if (value == null || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function sumDrillzRows(rows) {
  return rows.reduce(
    (acc, row) => ({
      metersDrilled: acc.metersDrilled + parseDrillzNumber(row.meters_drilled),
      metersLogged: acc.metersLogged + parseDrillzNumber(row.meters_logged),
      metersCut: acc.metersCut + parseDrillzNumber(row.meters_cut),
      samplesDesignated: acc.samplesDesignated + parseDrillzInt(row.samples_designated),
      samplesInBag: acc.samplesInBag + parseDrillzInt(row.samples_in_bag),
    }),
    { metersDrilled: 0, metersLogged: 0, metersCut: 0, samplesDesignated: 0, samplesInBag: 0 }
  );
}

/**
 * Resolve Drillz campaign progress for the selected dashboard tab.
 * @returns {{ metersDrilled: number, metersLogged: number, metersCut: number, samplesDesignated: number, samplesInBag: number, source: 'drillz' | null }}
 */
export function resolveDrillzMetrics({ drillzRows, projectSources, selectedProjectId }) {
  if (!Array.isArray(drillzRows) || drillzRows.length === 0) {
    return { metersDrilled: 0, metersLogged: 0, metersCut: 0, samplesDesignated: 0, samplesInBag: 0, source: null };
  }

  const rowsByCampaignId = new Map(
    drillzRows
      .filter((row) => row?.campaign_id != null && String(row.campaign_id).trim() !== '')
      .map((row) => [String(row.campaign_id), row])
  );

  if (selectedProjectId === 'all') {
    const campaignIds = (projectSources || [])
      .map((src) => src.drillz_campaign_id)
      .filter((id) => id != null && String(id).trim() !== '')
      .map((id) => String(id));

    const matchedRows = campaignIds
      .map((id) => rowsByCampaignId.get(id))
      .filter(Boolean);

    if (!matchedRows.length) {
      return { metersDrilled: 0, metersLogged: 0, metersCut: 0, samplesDesignated: 0, samplesInBag: 0, source: null };
    }

    return { ...sumDrillzRows(matchedRows), source: 'drillz' };
  }

  const project = (projectSources || []).find(
    (src) => String(src.project_id) === String(selectedProjectId)
  );
  const campaignId = project?.drillz_campaign_id;
  if (campaignId == null || String(campaignId).trim() === '') {
    return { metersDrilled: 0, metersLogged: 0, metersCut: 0, samplesDesignated: 0, samplesInBag: 0, source: null };
  }

  const row = rowsByCampaignId.get(String(campaignId));
  if (!row) {
    return { metersDrilled: 0, metersLogged: 0, metersCut: 0, samplesDesignated: 0, samplesInBag: 0, source: null };
  }

  return {
    metersDrilled: parseDrillzNumber(row.meters_drilled),
    metersLogged: parseDrillzNumber(row.meters_logged),
    metersCut: parseDrillzNumber(row.meters_cut),
    samplesDesignated: parseDrillzInt(row.samples_designated),
    samplesInBag: parseDrillzInt(row.samples_in_bag),
    source: 'drillz',
  };
}
